import { useTranslation } from 'react-i18next';
import NadaConsta from '../../../../components/nadaConsta';
import {
  BlocoGenerico,
  Badge,
  Campos,
  CamposObjeto,
  Container,
  Mensagem,
  Resumos,
  Score,
  Secao,
  Tabela,
  formatarCentavos,
  formatarData,
  formatarDocumento,
  formatarTexto,
  rotulo,
} from './ui';

// Blocos do "organizado" com layout próprio; o restante é exibido de forma genérica
const BLOCOS_TRATADOS = [
  'versao', 'produto', 'documento', 'consultado_em', 'veredito', 'score', 'titular', 'restricoes',
  'adicional_spc', 'renda', 'vinculos', 'consultas', 'documentos_roubados', 'contatos', 'outras_secoes', 'avisos',
];

const temConteudo = (situacao?: string) => situacao === 'com_registro';

export function SpcSerasa({ data, isConsultaNova }: { data: any; isConsultaNova?: boolean }) {
  const { t } = useTranslation();

  const org = data?.organizado ?? {};
  const documento = org.documento ?? {};
  const veredito = org.veredito ?? {};
  const score = org.score ?? {};
  const titular = org.titular ?? {};
  const restricoes = Object.entries(org.restricoes ?? {}) as [string, any][];
  const restricoesComRegistro = restricoes.filter(([, r]) => r?.quantidade > 0 || r?.registros?.length);
  const spc = org.adicional_spc;
  const renda = org.renda;
  const vinculos = org.vinculos;
  const consultas = org.consultas;
  const contatos = org.contatos ?? {};
  const roubados = org.documentos_roubados;
  const outrosBlocos = Object.entries(org).filter(([chave]) => !BLOCOS_TRATADOS.includes(chave));
  const isPJ = documento.tipo === 'cnpj' || data?.tipo_pessoa === 'PJ';
  const comRestricao = veredito.situacao ? veredito.situacao !== 'sem_restricao' : restricoesComRegistro.length > 0;

  const naturalidade = titular.naturalidade;
  const identidade = titular.documento_identidade;

  return (
    <Container
      titulo={t('creditoBasicoPlus')}
      isConsultaNova={isConsultaNova}
      alerta={comRestricao}
      badge={<Badge texto={!comRestricao ? 'Sem restrições' : veredito.situacao ? formatarTexto(veredito.situacao) : 'Possui restrições'} tom={comRestricao ? 'vermelho' : 'verde'} />}
    >
      <Campos
        campos={[
          { label: isPJ ? 'CNPJ' : 'CPF', value: formatarDocumento(documento.numero ?? data?.documento) },
          { label: 'Data da consulta', value: formatarData(org.consultado_em ?? data?.consultado_em) },
        ]}
      />
      <div style={{ marginTop: '16px' }}>
        <CamposObjeto objeto={titular} />
      </div>
      {(naturalidade || identidade) && (
        <div style={{ marginTop: '16px' }}>
          <Campos
            campos={[
              { label: 'Naturalidade', value: naturalidade && [naturalidade.cidade, naturalidade.uf].filter(Boolean).join('/') },
              { label: 'Documento de identidade', value: identidade?.numero && `${identidade.tipo ?? ''} ${identidade.numero}`.trim() },
              { label: 'Órgão emissor', value: identidade?.orgao_emissor && [identidade.orgao_emissor, identidade.uf].filter(Boolean).join('/') },
              { label: 'Data de emissão', value: identidade?.data_emissao && formatarData(identidade.data_emissao) },
            ]}
          />
        </div>
      )}

      <Secao titulo="Score">
        <Score
          valor={score.situacao === 'calculado' ? score.valor : null}
          minimo={score.escala?.min}
          maximo={score.escala?.max}
          faixa={score.faixa}
          mensagem={score.leitura || data?.relatorio?.score?.message}
          detalhes={[
            { label: 'Chance de pagamento', value: score.chance_pagamento_pct != null ? `${score.chance_pagamento_pct}%` : null },
            { label: 'Probabilidade de inadimplência', value: score.inadimplencia_pct != null ? `${score.inadimplencia_pct}%` : null },
            { label: 'Leitura', value: score.leitura },
          ]}
        />
      </Secao>

      <Secao titulo="Restrições">
        {!comRestricao && !restricoesComRegistro.length ? (
          <NadaConsta />
        ) : (
          <Resumos
            itens={[
              { label: 'Apontamentos', value: veredito.apontamentos ?? 0, icon: 'pi pi-file' },
              { label: 'Protestos', value: veredito.protestos ?? 0, icon: 'pi pi-briefcase' },
              { label: 'Total em dívidas', value: formatarCentavos(veredito.total_dividas_cents), icon: 'pi pi-dollar' },
            ]}
          />
        )}
      </Secao>

      {restricoes.length > 0 && (
        <Secao titulo="Resumo por natureza">
          <Tabela
            linhas={restricoes.map(([chave, r]) => ({ chave, ...r }))}
            colunas={[
              { chave: 'natureza', titulo: 'Natureza', render: r => r.natureza ?? rotulo(r.chave) },
              { chave: 'situacao', titulo: 'Situação' },
              { chave: 'quantidade', titulo: 'Quantidade' },
              { chave: 'valor_total_cents', titulo: 'Valor', render: r => (r.valor_total_cents ? formatarCentavos(r.valor_total_cents) : '—') },
            ]}
          />
        </Secao>
      )}

      {restricoesComRegistro.map(([chave, r]) => (
        <Secao
          key={chave}
          titulo={r.natureza ?? rotulo(chave)}
          extra={<Badge texto={`${r.quantidade ?? r.registros.length} · ${formatarCentavos(r.valor_total_cents)}`} tom="vermelho" />}
        >
          {r.lista_parcial && <Mensagem texto="Lista parcial de registros." />}
          <Tabela linhas={r.registros ?? []} />
        </Secao>
      ))}

      {spc && (
        <Secao titulo="Anotações e consultas adicionais" extra={<Badge texto={formatarTexto(spc.situacao)} tom={temConteudo(spc.situacao) ? 'vermelho' : 'amarelo'} />}>
          {/* A mensagem da API cita o nome do bureau, que não deve aparecer para o cliente */}
          <Mensagem texto={spc.mensagem?.replace(/\b(SPC|Serasa)\b/gi, 'bureau')} />
          <Campos
            campos={[
              { label: 'Anotações', value: spc.anotacoes?.quantidade ?? formatarTexto(spc.anotacoes?.situacao) },
              { label: 'Consultas', value: spc.consultas?.quantidade ?? formatarTexto(spc.consultas?.situacao) },
            ]}
          />
          {spc.anotacoes?.registros?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela linhas={spc.anotacoes.registros} />
            </div>
          )}
          {spc.consultas?.registros?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela linhas={spc.consultas.registros} />
            </div>
          )}
        </Secao>
      )}

      {renda && temConteudo(renda.situacao) && (
        <Secao titulo="Renda e capacidade de pagamento">
          <CamposObjeto objeto={renda} ignorar={['situacao']} />
        </Secao>
      )}

      {vinculos && temConteudo(vinculos.situacao) && (
        <Secao titulo={rotulo(vinculos.tipo ?? 'vinculos')}>
          {Object.entries(vinculos)
            .filter(([, v]) => Array.isArray(v) && v.length)
            .map(([chave, v]) => (
              <div key={chave} style={{ marginBottom: '12px' }}>
                <Tabela linhas={v as any[]} />
              </div>
            ))}
        </Secao>
      )}

      {consultas && temConteudo(consultas.situacao) && (
        <Secao titulo="Consultas ao documento">
          {consultas.recentes?.length > 0 && <Tabela linhas={consultas.recentes} />}
          {consultas.por_mes?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela linhas={consultas.por_mes} />
            </div>
          )}
          {consultas.por_mes_cheque?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Consultas por cheque</span>
              <Tabela linhas={consultas.por_mes_cheque} />
            </div>
          )}
        </Secao>
      )}

      {roubados && temConteudo(roubados.situacao) && (
        <Secao titulo="Documentos roubados/furtados">
          <Tabela linhas={roubados.registros ?? []} />
        </Secao>
      )}

      {(contatos.telefones?.length > 0 || contatos.enderecos?.length > 0) && (
        <Secao titulo="Contatos">
          {contatos.telefones?.length > 0 && (
            <Tabela
              linhas={contatos.telefones}
              colunas={[
                { chave: 'tipo', titulo: 'Tipo' },
                { chave: 'telefone', titulo: 'Telefone', render: r => `(${r.ddd}) ${r.numero}` },
                { chave: 'atualizado_em', titulo: 'Atualizado em' },
              ]}
            />
          )}
          {contatos.enderecos?.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <Tabela
                linhas={contatos.enderecos}
                colunas={[
                  {
                    chave: 'endereco',
                    titulo: 'Endereço',
                    render: r => [r.logradouro, r.numero, r.complemento].filter(Boolean).join(', '),
                  },
                  { chave: 'bairro', titulo: 'Bairro' },
                  { chave: 'cidade', titulo: 'Cidade', render: r => [r.cidade, r.uf].filter(Boolean).join('/') },
                  { chave: 'cep', titulo: 'CEP' },
                  { chave: 'atualizado_em', titulo: 'Atualizado em' },
                ]}
              />
            </div>
          )}
        </Secao>
      )}

      {outrosBlocos.map(([chave, valor]) => (
        <BlocoGenerico key={chave} titulo={rotulo(chave)} valor={valor} />
      ))}

      {(org.outras_secoes ?? []).map((secao: any, idx: number) => (
        <BlocoGenerico key={idx} titulo={secao?.titulo ?? 'Outras informações'} valor={secao} />
      ))}

      {org.avisos?.length > 0 && <BlocoGenerico titulo="Avisos" valor={org.avisos} />}
    </Container>
  );
}
